import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Tab } from "@headlessui/react";
import Split from "react-split";
import useQuery from "../../hooks/useQuery";

export const Tabs = React.memo((props) => {
  const useSearchParams = props.useSearchParams;
  const queries = useQuery();

  const id = props.id || "tabMenu";
  const queryId = queries.get(id);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollable, setScrollable] = useState(true);

  useEffect(() => {
    if (props.data && props.data[selectedIndex]) {
      setScrollable(props.data[selectedIndex].scrollable !== false);
    }
  }, [selectedIndex, props.data]);

  // Allow callers to re-measure/adjust layout on tab changes (ex: conditional padding).
  useEffect(() => {
    if (!props.onPanelsLayout) return;
    const raf = requestAnimationFrame(() => {
      props.onPanelsLayout({
        selectedIndex,
        selectedTab: props.data?.[selectedIndex],
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedIndex, props.data, props.onPanelsLayout]);

  function handleTabClick(e, item) {
    let path = "";
    if (useSearchParams) {
      const params = new URLSearchParams({
        ...Object.fromEntries(queries),
        [id]: item.id || item.title,
      });
      path = location.pathname + "?" + params.toString() + location.hash;
    }
    props.onClick && props.onClick(e, item, path);
    return;
  }

  useEffect(() => {
    const def = getDefaultTab();
    if (!def || !props.data?.length) return;

    const defTabIdx = props.data.findIndex((tab) => tab.id === def);
    if (defTabIdx >= 0) {
      setSelectedIndex(defTabIdx);
    }
  }, [queryId, props.data]);

  useEffect(() => {
    if (!props.selectedId || !props.data?.length) return;
    const nextIndex = props.data.findIndex((tab) => tab.id === props.selectedId);
    if (nextIndex >= 0 && nextIndex !== selectedIndex) {
      setSelectedIndex(nextIndex);
    }
  }, [props.selectedId, props.data, selectedIndex]);

  function getDefaultTab() {
    const menuQuery = queryId;
    return menuQuery;
  }

  function renderPanels({ nested } = {}) {
    return (
      <Tab.Panels
        as="div"
        ref={props.panelsRef}
        // Prefer flex sizing over hard-coded height math. This prevents subtle
        // border/padding/rounding overflows that show up as 1–2px "outer scroll".
        style={props.panelStyle}
        className={classNames(
          "w-full min-h-0",
          nested ? "flex-1" : "h-full",
          props.panelClasses,
          {
            // In fullHeight mode, make panels a flex column so the active panel
            // can truly fill the available space without relying on % heights.
            "flex flex-col flex-1": props.fullHeight,
          }
        )}
      >
        {props.data &&
          props.data.map((item, i) => {
            return (
              <Tab.Panel
                key={i}
                as="div"
                className={classNames("w-full", props.panelItemClasses, {
                  "flex-1 min-h-0": props.fullHeight,
                })}
              >
                {item.content}
              </Tab.Panel>
            );
          })}
      </Tab.Panels>
    );
  }

  function renderTabs() {
    const tabMap = props.data;
    const tabsChildren = tabMap.map((item, i) => {
      return (
        <Tab
          key={i}
          onClick={(e) =>
            item.onClick ? item.onClick(e, item) : handleTabClick(e, item)
          }
          className={({ selected }) => {
            const isActive = selected;
            const classes = props.tabContentClassName
              ? props.tabContentClassName(isActive)
              : classNames(
                  props.tabClass,
                  "inline-flex p-2 group focus:outline-non",
                  "rounded-t-lg border-b-2 border-transparent text-sm font-semibold focus-visible:outline-0",
                  {
                    "text-primary-600 !border-primary-600 dark:text-primary-500 dark:border-primary-500":
                      isActive,
                  },
                  {
                    "hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300 text-gray-700":
                      !isActive,
                  }
                );
            return classes;
          }}
        >
          {item.title || item.id}
        </Tab>
      );
    });

    const tabs = (
      <Tab.List className={props.tabListClasses}>{tabsChildren}</Tab.List>
    );

    if (props.tabHeader) {
      return props.tabHeader(tabs);
    }
    return tabs;
  }

  return (
    <Tab.Group
      className={classNames("w-full", props.className, {
        // `min-h-0` is critical when Tabs is inside a constrained flex parent;
        // without it, panels can overflow by a few pixels and create a second
        // scrollbar on the outer container.
        "h-full min-h-0 flex flex-col": props.fullHeight,
      })}
      style={props.style}
      as="div"
      defaultIndex={selectedIndex || props.default}
      selectedIndex={selectedIndex}
      onChange={(nextIndex) => {
        setSelectedIndex(nextIndex);
        props.onChangeTab?.(props.data?.[nextIndex], nextIndex);
      }}
    >
      {props.aside ? (
        <Split
          className="split split-horizontal w-full min-h-0 flex-1"
          direction="horizontal"
          sizes={[50, 50]}
          minSize={[280, 320]}
          gutterSize={8}
          snapOffset={0}
        >
          <div className="h-full min-h-0 overflow-hidden border-r bg-white">
            {props.aside}
          </div>
          <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
            {renderTabs()}
            {renderPanels({ nested: true })}
          </div>
        </Split>
      ) : (
        <>
          {renderTabs()}
          {renderPanels()}
        </>
      )}
      {props.footer && props.footer(props)}
    </Tab.Group>
  );
});

export default Tabs;
